import { useEffect, useRef } from 'react';
import { useGoogleMap } from '@react-google-maps/api';

function criarPaleta(gradiente) {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 1;
  const ctx = canvas.getContext('2d');
  const linear = ctx.createLinearGradient(0, 0, 256, 0);
  gradiente.forEach((cor, indice) => {
    linear.addColorStop(indice / Math.max(gradiente.length - 1, 1), cor);
  });
  ctx.fillStyle = linear;
  ctx.fillRect(0, 0, 256, 1);
  return ctx.getImageData(0, 0, 256, 1).data;
}

function colorirIntensidade(alpha, paleta, opacidade) {
  const i = Math.min(255, Math.max(0, Math.round(alpha))) * 4;
  return [
    paleta[i],
    paleta[i + 1],
    paleta[i + 2],
    Math.round((paleta[i + 3] / 255) * opacidade * 255),
  ];
}

function criarClasseOverlay(google) {
  /* eslint-disable no-new-func */
  return class CanvasHeatmapOverlay extends google.maps.OverlayView {
    constructor({ points, gradient, radius, opacity }) {
      super();
      this.points = points;
      this.gradient = gradient;
      this.radius = radius;
      this.opacity = opacity;
      this.canvas = null;
      this.paleta = criarPaleta(gradient);
    }

    onAdd() {
      this.canvas = document.createElement('canvas');
      this.canvas.style.position = 'absolute';
      this.canvas.style.pointerEvents = 'none';
      this.getPanes().overlayLayer.appendChild(this.canvas);
    }

    draw() {
      if (!this.canvas || !this.getMap()) return;
      const projection = this.getProjection();
      if (!projection) return;

      const bounds = this.getMap().getBounds();
      if (!bounds) return;

      // Usa coordenadas do sistema "divPixel" — mesmo sistema do overlayLayer
      const ne = projection.fromLatLngToDivPixel(bounds.getNorthEast());
      const sw = projection.fromLatLngToDivPixel(bounds.getSouthWest());
      if (!ne || !sw) return;

      const left = Math.floor(Math.min(ne.x, sw.x));
      const top = Math.floor(Math.min(ne.y, sw.y));
      const width = Math.ceil(Math.abs(ne.x - sw.x));
      const height = Math.ceil(Math.abs(sw.y - ne.y));

      if (!width || !height) return;

      this.canvas.style.left = `${left}px`;
      this.canvas.style.top = `${top}px`;
      this.canvas.width = width;
      this.canvas.height = height;
      this.canvas.style.width = `${width}px`;
      this.canvas.style.height = `${height}px`;

      // Canvas temporário de intensidade
      const canvasInt = document.createElement('canvas');
      canvasInt.width = width;
      canvasInt.height = height;
      const ctxInt = canvasInt.getContext('2d');

      this.points.forEach((point) => {
        const pixel = projection.fromLatLngToDivPixel(
          new google.maps.LatLng(point.lat, point.lng)
        );
        if (!pixel) return;

        // Coordenada relativa ao canvas (subtrai offset do canto NW)
        const px = pixel.x - left;
        const py = pixel.y - top;
        const peso = point.weight ?? 1;
        const raio = this.radius;

        const grad = ctxInt.createRadialGradient(px, py, 0, px, py, raio);
        grad.addColorStop(0, `rgba(0,0,0,${Math.min(peso / 2, 1) * 0.6})`);
        grad.addColorStop(1, 'rgba(0,0,0,0)');

        ctxInt.globalCompositeOperation = 'lighter';
        ctxInt.fillStyle = grad;
        ctxInt.beginPath();
        ctxInt.arc(px, py, raio, 0, Math.PI * 2);
        ctxInt.fill();
      });

      const ctx = this.canvas.getContext('2d');
      ctx.clearRect(0, 0, width, height);

      const imageData = ctxInt.getImageData(0, 0, width, height);
      const dados = imageData.data;

      for (let i = 0; i < dados.length; i += 4) {
        const alpha = dados[i + 3];
        if (alpha <= 0) continue;
        const [r, g, b, a] = colorirIntensidade(alpha, this.paleta, this.opacity);
        dados[i] = r;
        dados[i + 1] = g;
        dados[i + 2] = b;
        dados[i + 3] = a;
      }

      ctx.putImageData(imageData, 0, 0);
    }

    onRemove() {
      if (this.canvas?.parentNode) {
        this.canvas.parentNode.removeChild(this.canvas);
      }
      this.canvas = null;
    }

    atualizar({ points, gradient, radius, opacity }) {
      this.points = points;
      this.gradient = gradient;
      this.radius = radius;
      this.opacity = opacity;
      this.paleta = criarPaleta(gradient);
      this.draw();
    }
  };
}

/**
 * Heatmap em canvas sobre o Google Maps.
 * Substitui google.maps.visualization.HeatmapLayer (removido na API 3.65).
 * Usa o sistema de coordenadas divPixel do OverlayView para posicionamento correto.
 */
export default function CanvasHeatmapOverlay({
  points = [],
  gradient,
  radius = 40,
  opacity = 0.75,
}) {
  const map = useGoogleMap();
  const overlayRef = useRef(null);
  const classeRef = useRef(null);

  useEffect(() => {
    if (!map || !window.google?.maps) return undefined;

    if (!points.length) {
      if (overlayRef.current) {
        overlayRef.current.setMap(null);
        overlayRef.current = null;
      }
      return undefined;
    }

    if (!classeRef.current) {
      classeRef.current = criarClasseOverlay(window.google);
    }

    const OverlayClass = classeRef.current;
    let overlay = overlayRef.current;

    if (!overlay) {
      overlay = new OverlayClass({ points, gradient, radius, opacity });
      overlay.setMap(map);
      overlayRef.current = overlay;
    } else {
      overlay.atualizar({ points, gradient, radius, opacity });
    }

    const listeners = [
      map.addListener('idle', () => overlay.draw()),
      map.addListener('zoom_changed', () => overlay.draw()),
      map.addListener('dragend', () => overlay.draw()),
      map.addListener('bounds_changed', () => overlay.draw()),
    ];

    return () => {
      listeners.forEach((l) => l.remove());
      overlay.setMap(null);
      overlayRef.current = null;
    };
  }, [map, points, gradient, radius, opacity]);

  if (!points.length) return null;
  return <span data-testid="heatmap" data-points={points.length} hidden aria-hidden="true" />;
}
